import { motion } from "framer-motion";
import { BarChart3, ClipboardCheck, FileCheck2, HeartHandshake, Scale } from "lucide-react";
import Reveal, { Eyebrow } from "./Reveal";

const FACILITY_IMG =
  "https://images.unsplash.com/photo-1626105985445-6430a31f6f96?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBoZWFsdGhjYXJlJTIwY29uc3VsdGluZyUyMG9mZmljZSUyMGJ1c2luZXNzJTIwbWVldGluZ3xlbnwwfHx8fDE3OTAzODc3MjV8MA&ixlib=rb-4.1.0&q=85";

const cardClass =
  "group glass-card relative overflow-hidden rounded-2xl p-8 transition-[transform,border-color] duration-500 hover:-translate-y-1.5 hover:border-ochre/40";

const iconClass =
  "mb-6 flex h-12 w-12 items-center justify-center rounded-xl border border-ochre/30 bg-ochre/10 text-ochre transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3";

const services = [
  {
    id: "poc",
    num: "02",
    icon: ClipboardCheck,
    title: "Plan of Correction (POC)",
    body: "We read your citation data line by line and draft Plans of Correction that surveyors accept — precise, defensible, and filed on deadline.",
  },
  {
    id: "poi",
    num: "03",
    icon: FileCheck2,
    title: "Plan of Implementation (POI)",
    body: "A correction plan is only paper until it is executed. We build the implementation roadmap and stay with you until compliance is verified.",
  },
  {
    id: "saps",
    num: "04",
    icon: HeartHandshake,
    title: "SAPS Development",
    body: "Individualized Service Action Plans built around each resident — clinically sound, person-centered, and audit-ready.",
  },
  {
    id: "data",
    num: "05",
    icon: BarChart3,
    title: "Data Reading & Audit Analysis",
    body: "We decode your facility data to find the root cause of citations before regulators do — turning raw numbers into a remediation strategy.",
  },
];

export default function Services() {
  return (
    <section id="services" className="relative bg-basalt py-28 lg:py-40" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <Eyebrow>What we do</Eyebrow>
          <h2 className="mt-4 max-w-3xl font-display text-2xl font-bold tracking-tight text-sand sm:text-3xl lg:text-4xl" data-testid="services-heading">
            Every path back to compliance — under one roof.
          </h2>
          <p className="mt-5 max-w-2xl text-base text-mist">
            From the first citation to the final survey, Hale Pono handles the entire remediation
            lifecycle for large and small ICF &amp; IDD facilities.
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          <Reveal className="md:col-span-2 lg:row-span-2" delay={0.05}>
            <div className={`${cardClass} flex h-full min-h-[420px] flex-col justify-end border-ochre/30`} data-testid="service-card-doj">
              <img
                src={FACILITY_IMG}
                alt="Compliance team reviewing facility documentation"
                className="absolute inset-0 h-full w-full object-cover opacity-25 transition-[opacity,transform] duration-700 group-hover:scale-105 group-hover:opacity-35"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-basalt via-basalt/60 to-transparent" />
              <div className="relative">
                <span className="font-mono text-xs tracking-[0.3em] text-ochre">01</span>
                <div className={iconClass}>
                  <Scale size={22} />
                </div>
                <h3 className="font-display text-xl font-semibold text-sand sm:text-2xl lg:text-3xl">
                  DOJ Extensions &amp; Stays
                </h3>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-mist sm:text-base">
                  Our signature specialty. When the Department of Justice timeline is closing in, we
                  prepare and file extension and stay requests that get approved — buying your
                  facility the time it needs to correct course without shutting down.
                </p>
                <span className="mt-6 inline-block rounded-full border border-ochre/40 bg-ochre/10 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-ochre">
                  Signature Specialty
                </span>
              </div>
            </div>
          </Reveal>

          {services.map((s, i) => (
            <Reveal key={s.id} delay={0.1 + i * 0.07} className={s.id === "data" ? "md:col-span-2" : ""}>
              <div className={`${cardClass} h-full`} data-testid={`service-card-${s.id}`}>
                <div className="flex items-start justify-between">
                  <div className={iconClass}>
                    <s.icon size={22} />
                  </div>
                  <span className="font-mono text-xs tracking-[0.3em] text-smoke">{s.num}</span>
                </div>
                <h3 className="font-display text-xl font-semibold text-sand">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-mist">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
